/**
 * Корень адресов делится на две части: сегменты САЙТА и всё остальное.
 *
 * С 29.09.2026 адрес любого аккаунта — `hub4psy.com/{username}` (и
 * `/ru/{username}`), без `/p/`. Значит, первый сегмент пути, который не
 * принадлежит сайту, — это чья-то страница, и три места обязаны отвечать на
 * вопрос «чей это адрес» ОДИНАКОВО: аналитика (`shared/analytics/sanitize.ts`
 * шаблонизирует имя в `:username`), мета (`app/seo.ts` даёт странице человека
 * описание каталога, пока она не загрузилась) и экран подтверждения почты
 * (`auth/pending-next.ts` узнаёт «вернуться к психологу»). Список здесь один;
 * его полноту сторожит `app/reserved-handles.test.ts`: каждый корневой сегмент
 * роутера обязан лежать здесь — и, если его можно занять как имя, в `RESERVED`
 * (`Usernames.java`).
 *
 * Модуль без импортов намеренно: его читает и аналитика, и роутер, и сборка
 * статики, а общий модуль, который тянет за собой роутер, стал бы циклом.
 */
export const SITE_ROOT_SEGMENTS: ReadonlySet<string> = new Set([
  // Русское дерево витрины: `/ru/<то же самое>`.
  'ru',
  // Витрина (marketingChildrenFor в app/router.tsx).
  'practice',
  'courses',
  'books',
  'classification',
  'app',
  'alphabet',
  'certification',
  'find-psychologist',
  'about',
  'contact',
  'privacy',
  'terms',
  // Старый адрес страницы психолога — вечная переадресация.
  'p',
  // Зоны и страницы на корне.
  'cabinet',
  'my',
  'design-system',
  'psymodeler',
  'language',
  's',
  // Вход и его привычные псевдонимы.
  'signin',
  'register',
  'verify-email',
  'forgot-password',
  'reset-password',
  'login',
  'sign-in',
  'signup',
  'sign-up',
  // Не маршруты приложения, но и не человек: API и бандл живут под корнем.
  'api',
  'assets',
])

/**
 * Первый сегмент пути после необязательного `/ru` — или пустая строка у корня.
 * `/ru/anna.k` → `anna.k`, `/courses/` → `courses`, `/` и `/ru` → ``.
 */
export function rootSegment(path: string): string {
  const parts = path.split(/[?#]/, 1)[0].split('/').filter(Boolean)
  const index = parts[0] === 'ru' ? 1 : 0
  return parts[index] ?? ''
}

/**
 * Чья-то страница: путь из ОДНОГО сегмента (после `/ru`), который сайту не
 * принадлежит. Возвращает имя как оно написано в адресе — форму имени решает
 * сервер (`Usernames.forLookup`), здесь только «не наш ли это раздел».
 * `/anna.k/extra` — не страница: у неё нет второго уровня.
 */
export function userPageHandle(path: string): string | null {
  const parts = path.split(/[?#]/, 1)[0].split('/').filter(Boolean)
  const index = parts[0] === 'ru' ? 1 : 0
  if (parts.length !== index + 1) return null
  const segment = parts[index]
  return segment && !SITE_ROOT_SEGMENTS.has(segment) ? segment : null
}
